import { PREVIEWABLE_IMAGE_EXTENSIONS, UploadedFile } from '@99designs/design-services-common';
import { StructuredBriefFile } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';

const DEFAULT_MAX_FILE_SIZE = 10000000; // 10MB
const BYTES_PER_KB = 1000;
const BYTES_PER_MB = 1000000;
const BYTES_PER_GB = 1000000000;
const MAX_FILE_NAME_LENGTH = 16;

/**
 * Resolve the maximum file size in bytes.
 *
 * @param maxFileSize {number} the limit in bytes reported by the backend
 */
function resolveMaxFileSize(maxFileSize: number) {
    return maxFileSize || DEFAULT_MAX_FILE_SIZE;
}

/**
 * Format a max file size in bytes as a localized "value + unit" string, e.g. "40 MB" or "1 GB".
 *
 * The unit name and its placement/spacing are locale-specific (e.g. French "Mo", Finnish "Mt"),
 * so this defers to `Intl.NumberFormat`'s `unit` style rather than hardcoding an abbreviation.
 *
 * @param maxFileSize {number} the limit in bytes reported by the backend
 * @param locale {string} the locale to format the value for
 */
export function formatMaxFileSize(maxFileSize: number, locale: string): string {
    const bytes = resolveMaxFileSize(maxFileSize);

    let unit: 'kilobyte' | 'megabyte' | 'gigabyte';
    let divisor: number;

    if (bytes >= BYTES_PER_GB) {
        unit = 'gigabyte';
        divisor = BYTES_PER_GB;
    } else if (bytes >= BYTES_PER_MB) {
        unit = 'megabyte';
        divisor = BYTES_PER_MB;
    } else {
        unit = 'kilobyte';
        divisor = BYTES_PER_KB;
    }

    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: 1,
    }).format(bytes / divisor);
}

export function mapErrorMessages(allowedFileTypes: string[], maxFileSizeWithUnit: string): string {
    if (allowedFileTypes.length === 0) {
        return __('Only files under {{maxFileSizeWithUnit}} are supported', {
            maxFileSizeWithUnit,
        });
    }

    const allowedTypes = [...allowedFileTypes].map((ext) => ext.replace('.', ''));

    if (allowedFileTypes.length === 1) {
        return __('Only {{fileType}} files under {{maxFileSizeWithUnit}} are supported', {
            fileType: allowedTypes[0],
            maxFileSizeWithUnit,
        });
    }

    const lastFileType = allowedTypes.pop();
    const fileTypes = allowedTypes.join(', ');
    return __(
        'Only {{fileTypes}} and {{lastFileType}} files under {{maxFileSizeWithUnit}} are supported',
        {
            fileTypes,
            lastFileType,
            maxFileSizeWithUnit,
        }
    );
}

export function filterFiles(
    files: FileList | null,
    allowedFileTypes: string[],
    maxFileSize: number
) {
    if (!files) {
        return [];
    }

    const maxSizeBytes = resolveMaxFileSize(maxFileSize);

    if (allowedFileTypes.length === 0) {
        return Array.from(files).filter((file) => file.size < maxSizeBytes);
    }

    return Array.from(files).filter((file) => {
        const fileExtension = file.name.split('.').pop();
        return allowedFileTypes.includes(`.${fileExtension}`) && file.size < maxSizeBytes;
    });
}

/**
 * Map the response from the MCP upload bucket to the StructuredBriefFile type.
 *
 * @param files {UploadedFile[]} the files to map
 */
export function mapUploadFilesToBriefFiles(files: UploadedFile[]): StructuredBriefFile[] {
    return files.map((file) => {
        const isPreviewable = PREVIEWABLE_IMAGE_EXTENSIONS.includes(
            file.name.split('.').pop()?.toLowerCase() || ''
        );

        return {
            __typename: 'StructuredBriefFile',
            size: parseInt(file.originalFileSize) || 0, // Provide a default value in case of parsing error.
            bucket: 'CimpressDocs',
            key: file.id,
            name: file.name,
            downloadUrl: file.downloadUrl,
            previewUrl: isPreviewable ? file.previewUrl : null,
            previewable: isPreviewable,
        };
    });
}

/**
 * Get a unique key for a file based on its name, size and last modified date.
 *
 * ## Notes
 *
 * This might not be as reliable as a hash of the binary content, but the use case is only to
 * track and remove files from the "uploading" state once they are added to the "uploaded" state.
 *
 * Even on collision, this will only cause issues if the user uploads a file, then uploads another
 * file with the colliding key before the first file is fully uploaded.
 *
 * The odds of this happening are low, and the impact is minimal.
 *
 * @param file {File} the file that is uploading
 */
export function mapFileToFileKey(file: File) {
    return `${file.name}-${file.size}-${file.lastModified}`;
}

/**
 * Truncate a filename, leaving always the extension, first three characters and last three
 * characters. If there is enough space, it will add more characters at the front.
 *
 * @param filename {string} the filename to truncate
 */
export function truncateFileName(filename: string) {
    if (filename.length < MAX_FILE_NAME_LENGTH) {
        return filename;
    }

    const filenameParts = filename.split('.');
    const extension = filenameParts.pop() || '';
    const filenameSansExtension = filenameParts.join('.');

    const truncatedNameFirstPart = filenameSansExtension.slice(
        0,
        MAX_FILE_NAME_LENGTH - extension.length - 6
    );
    const truncatedNameLastPart = filenameSansExtension.slice(-3);
    const truncatedName = `${truncatedNameFirstPart}...${truncatedNameLastPart}`;

    return `${truncatedName}.${extension}`;
}
